import type {OttopilotEventContainer} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {
    computeNotePlainTextLength,
    createNoteDraft,
    readEventContainerNotes,
    removeNoteFromList,
    serializeNotesForApi,
    upsertNoteInList,
    type ListNotesCreateScope,
    type Note,
    type NoteAttachment,
} from "~/domainModels/notes";
import {NOTES_FIELD} from "~/domainModels/notesFields";
import {isAmaronWorkspace} from "~/domainModels/workspaceSpecific/amaron";
import {isDuluxBlueStoreLeadEventContainer, isDuluxWorkspace} from "~/domainModels/workspaceSpecific/dulux";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    amaronCreateEventContainerNoteAction,
    type AmaronEventContainerNoteResponse,
    amaronDeleteEventContainerNoteAction,
    amaronUpdateEventContainerNoteAction,
} from "~/utilities/apis/amaronActionsApi";
import {
    duluxBlueStoreCreateEventContainerNoteAction,
    duluxBlueStoreDeleteEventContainerNoteAction,
    duluxBlueStoreUpdateEventContainerNoteAction,
    duluxCreateEventContainerNoteAction,
    type DuluxEventContainerNoteResponse,
    duluxDeleteEventContainerNoteAction,
    duluxUpdateEventContainerNoteAction,
} from "~/utilities/apis/duluxActionsApi";
import {updateEventContainer} from "~/utilities/apis/eventContainersApi";
import type {WorkspaceCapability} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";

export async function persistEventContainerNotes({
    accessToken,
    intellsysBaseUrl,
    workspaceId,
    eventContainerId,
    eventContainerTemplateId,
    nextNotes,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    eventContainerId: Uuid;
    eventContainerTemplateId: Uuid;
    nextNotes: Array<Note>;
}) {
    const result = await updateEventContainer({
        accessToken,
        intellsysBaseUrl,
        workspaceId,
        id: eventContainerId,
        templateId: eventContainerTemplateId,
        data: {
            [NOTES_FIELD]: serializeNotesForApi(nextNotes),
        } as never,
    });
    if (result.success !== true) {
        throw result.error;
    }
}

export async function saveNote({
    accessToken,
    intellsysBaseUrl,
    workspaceId,
    workspaceCapabilities,
    eventContainer,
    note,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    workspaceCapabilities: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>;
    eventContainer: OttopilotEventContainer;
    note: Note;
}): Promise<Note> {
    const shouldUseWorkspaceSpecificNotes =
        isAmaronWorkspace(workspaceCapabilities) === true ||
        isDuluxWorkspace(workspaceCapabilities) === true;
    const isDulux = isDuluxWorkspace(workspaceCapabilities);
    const isDuluxBlueStoreLead = isDulux === true && isDuluxBlueStoreLeadEventContainer(eventContainer);

    if (shouldUseWorkspaceSpecificNotes === true) {
        const notePayload = {
            bodyMarkdown: note.bodyMarkdown,
            attachments: note.attachments.map((attachment) => ({
                assetId: attachment.assetId,
                fileName: attachment.fileName,
                mimeType: attachment.mimeType ?? null,
                createdAt: attachment.createdAt,
            })),
            scopeType: "eventContainer" as const,
        };

        const existingNoteId = note.id;
        const currentNotes = readEventContainerNotes(eventContainer);
        const isExistingNote = currentNotes.some((currentNote) => currentNote.id === existingNoteId);

        if (isExistingNote === true) {
            const updateNoteAction = resolveWorkspaceSpecificNoteUpdateAction({
                isDulux: isDulux,
                isDuluxBlueStoreLead: isDuluxBlueStoreLead,
            });
            const updateResult = await updateNoteAction({
                accessToken: accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                request: {
                    workspaceId: workspaceId,
                    eventContainerId: eventContainer.id,
                    noteId: existingNoteId,
                    note: notePayload,
                },
            });
            if (updateResult.success !== true) {
                throw updateResult.error;
            }
            return mapWorkspaceSpecificEventContainerNoteResponseToNote(updateResult.data.note);
        }

        const createNoteAction = resolveWorkspaceSpecificNoteCreateAction({
            isDulux: isDulux,
            isDuluxBlueStoreLead: isDuluxBlueStoreLead,
        });
        const createResult = await createNoteAction({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            request: {
                workspaceId: workspaceId,
                eventContainerId: eventContainer.id,
                note: notePayload,
            },
        });
        if (createResult.success !== true) {
            throw createResult.error;
        }
        return mapWorkspaceSpecificEventContainerNoteResponseToNote(createResult.data.note);
    }

    const current = readEventContainerNotes(eventContainer);
    await persistEventContainerNotes({
        accessToken,
        intellsysBaseUrl,
        workspaceId,
        eventContainerId: eventContainer.id,
        eventContainerTemplateId: eventContainer.templateId,
        nextNotes: upsertNoteInList(current, note),
    });
    return note;
}

export async function deleteNote({
    accessToken,
    intellsysBaseUrl,
    workspaceId,
    workspaceCapabilities,
    eventContainer,
    note,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    workspaceCapabilities: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>;
    eventContainer: OttopilotEventContainer;
    note: Note;
}) {
    const isDulux = isDuluxWorkspace(workspaceCapabilities);
    const isDuluxBlueStoreLead = isDulux === true && isDuluxBlueStoreLeadEventContainer(eventContainer);
    if (isAmaronWorkspace(workspaceCapabilities) === true || isDulux === true) {
        const deleteNoteAction = resolveWorkspaceSpecificNoteDeleteAction({
            isDulux: isDulux,
            isDuluxBlueStoreLead: isDuluxBlueStoreLead,
        });
        const deleteResult = await deleteNoteAction({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            request: {
                workspaceId: workspaceId,
                eventContainerId: eventContainer.id,
                noteId: note.id,
            },
        });
        if (deleteResult.success !== true) {
            throw deleteResult.error;
        }
        return;
    }

    const current = readEventContainerNotes(eventContainer);
    await persistEventContainerNotes({
        accessToken,
        intellsysBaseUrl,
        workspaceId,
        eventContainerId: eventContainer.id,
        eventContainerTemplateId: eventContainer.templateId,
        nextNotes: removeNoteFromList(current, note.id),
    });
}

export function buildNoteForSave(args: {
    eventContainerId: Uuid;
    createScope: ListNotesCreateScope;
    bodyMarkdown: string;
    attachments: Array<NoteAttachment>;
    createdBy: Uuid;
    existingNote?: Note;
}): Note {
    if (args.existingNote !== undefined) {
        const now = new Date().toISOString();
        return {
            ...args.existingNote,
            bodyMarkdown: args.bodyMarkdown,
            bodyPlainTextLength: computeNotePlainTextLength(args.bodyMarkdown),
            attachments: args.attachments,
            updatedAt: now as Note["updatedAt"],
        };
    }
    return createNoteDraft({
        eventContainerId: args.eventContainerId,
        createScope: args.createScope,
        bodyMarkdown: args.bodyMarkdown,
        attachments: args.attachments,
        createdBy: args.createdBy,
    });
}

function mapWorkspaceSpecificEventContainerNoteResponseToNote(
    note: AmaronEventContainerNoteResponse | DuluxEventContainerNoteResponse,
): Note {
    const attachments = note.attachments.map((attachment) => ({
        assetId: attachment.assetId,
        fileName: attachment.fileName,
        mimeType: attachment.mimeType ?? undefined,
        createdAt: attachment.createdAt as NoteAttachment["createdAt"],
    }));
    if (note.scopeType === "event" && note.eventId !== null && note.eventTemplateId !== null) {
        return {
            id: note.id,
            eventContainerId: note.eventContainerId,
            bodyMarkdown: note.bodyMarkdown,
            bodyPlainTextLength: note.bodyPlainTextLength,
            createdBy: note.createdBy,
            createdAt: note.createdAt as Note["createdAt"],
            updatedAt: note.updatedAt as Note["updatedAt"],
            attachments: attachments,
            scopeType: "event",
            eventId: note.eventId,
            eventTemplateId: note.eventTemplateId,
        };
    }
    return {
        id: note.id,
        eventContainerId: note.eventContainerId,
        bodyMarkdown: note.bodyMarkdown,
        bodyPlainTextLength: note.bodyPlainTextLength,
        createdBy: note.createdBy,
        createdAt: note.createdAt as Note["createdAt"],
        updatedAt: note.updatedAt as Note["updatedAt"],
        attachments: attachments,
        scopeType: "eventContainer",
        eventId: null,
        eventTemplateId: null,
    };
}

type WorkspaceSpecificNoteActionContext = {
    isDulux: boolean;
    isDuluxBlueStoreLead: boolean;
};

function resolveWorkspaceSpecificNoteCreateAction(context: WorkspaceSpecificNoteActionContext) {
    if (context.isDuluxBlueStoreLead === true) {
        return duluxBlueStoreCreateEventContainerNoteAction;
    }
    if (context.isDulux === true) {
        return duluxCreateEventContainerNoteAction;
    }
    return amaronCreateEventContainerNoteAction;
}

function resolveWorkspaceSpecificNoteUpdateAction(context: WorkspaceSpecificNoteActionContext) {
    if (context.isDuluxBlueStoreLead === true) {
        return duluxBlueStoreUpdateEventContainerNoteAction;
    }
    if (context.isDulux === true) {
        return duluxUpdateEventContainerNoteAction;
    }
    return amaronUpdateEventContainerNoteAction;
}

function resolveWorkspaceSpecificNoteDeleteAction(context: WorkspaceSpecificNoteActionContext) {
    if (context.isDuluxBlueStoreLead === true) {
        return duluxBlueStoreDeleteEventContainerNoteAction;
    }
    if (context.isDulux === true) {
        return duluxDeleteEventContainerNoteAction;
    }
    return amaronDeleteEventContainerNoteAction;
}
